import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  setForecastVariable,
  setSavedForecastConfig,
} from '../store/mapPreferences';
import {
  selectForecastVariable,
  selectSavedForecastConfigId,
} from '../store/mapPreferences/selectors';
import {
  type ForecastModelKey,
  type ForecastVariableKey,
  forecastCatalogModels,
  forecastModelList,
  forecastRailVariables,
  pickAutoModel,
  premiumUpsellModel,
} from '../utils/WeatherMap/forecastCatalog';
import {
  FORECAST_NOTICE_TIMEOUT_MS,
  FORECAST_MAP_SELECTION_WORLD,
  type ForecastAutoPick,
  type ForecastLockedPick,
  type ForecastNoticeState,
  type ForecastMapSelectionUrl,
  type ForecastMapSelectionView,
  type ForecastUnavailable,
  autoSwitchedNotice,
  drawnForecastConfig,
  fallbackAutoModel,
  forecastLeftArea,
  forecastMapSelectionKey,
  forecastMapSelectionUrl,
  forecastMapSelectionVariable,
  forecastUnavailableBanner,
  resolveForecastMapSelection,
  splitForecastCatalog,
  usableForecastConfig,
  variableOnlyNotice,
} from '../utils/WeatherMap/forecastMapSelection';
import {forecastConfigRef} from '../utils/ForecastMap/configRef';
import useForecastMapCatalog from './useForecastMapCatalog';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type ForecastMapSelectionAdapters = {
  /** What the app's URL (or route) names: `config` and `variable`. */
  url: ForecastMapSelectionUrl;
  /**
   * Writes the selection into the app's URL, replacing the entry: a null
   * param is removed. The resolution is written back each time this
   * changes identity, so hand in one that changes when the URL does.
   */
  writeUrl: (next: ForecastMapSelectionUrl) => void;
  /** The view Auto works on: the app updates it when its map settles. */
  view: ForecastMapSelectionView;
  /**
   * Subscribes `dismiss` to the interactions that end a notice (on the web:
   * a press anywhere, the map starting to move); returns the unsubscribe.
   * Called while a notice shows; its identity is a dependency.
   */
  subscribeNoticeDismiss?: (dismiss: () => void) => () => void;
  /** A locked model or variable was chosen: offer Premium. */
  onLockedPick?: (pick: ForecastLockedPick) => void;
};

/**
 * Which forecast the map's forecast mode shows (#327 MR5d), and the picker's
 * state: the rail's variables, the chip's model, the model list, the
 * notices. Platform-free since step 4 S4-1: the app owns the map and the URL
 * and hands them in (`ForecastMapSelectionAdapters`); the rules live in
 * `utils/WeatherMap/forecastMapSelection`.
 *
 * Resolution, from the URL first (it is the state; the user's choices write
 * it, replace): a valid URL `config`, a URL `variable` (Auto), the config
 * saved on this device (saved only when hand-picked), Auto on the saved
 * variable (temperature by default). A stale saved config is forgotten
 * silently; an invalid URL config raises `unavailable` (the Banner), kept
 * once raised. The resolved selection is written back: Auto as `variable=`,
 * anything else as `config=`.
 *
 * Auto re-picks when the map settles (a new `view.seq`), with core
 * `pickAutoModel`'s hysteresis (in at 90%, out below 85%). A view-driven
 * switch raises the `autoSwitched` notice; choosing a variable only one
 * model has raises `variableOnly`. Both go after 6 s or on the next
 * interaction (`subscribeNoticeDismiss`, or a choice in the picker). The
 * Auto notice is raised only when the previous model covers < 85% of the
 * view. A hand-picked model covering less than 85% of a view the user moved
 * to (a new `view.userSeq`) gives `leftArea`, which stays.
 *
 * Locked presets (#327 MR7a): the catalog lists them too (`include_locked`),
 * marked `locked` by the backend for an account without the entitlement --
 * the account's plan is never read here. They are never opened (their
 * manifests 404): a locked URL config falls back to Auto with its own
 * Banner, a locked saved one is forgotten silently, and choosing a locked
 * model or variable calls `onLockedPick` without switching. The rail shows
 * the locked variables too, the list the locked models, and `upsellModel`
 * names the Premium model Auto would draw.
 */
const useForecastMapSelection = ({
  url,
  writeUrl,
  view,
  subscribeNoticeDismiss,
  onLockedPick,
}: ForecastMapSelectionAdapters) => {
  const dispatch = useDispatch();
  const catalog = useForecastMapCatalog({includeLocked: true});
  // Read at call time: the picks stay stable across renders.
  const lockedPickRef = React.useRef(onLockedPick);
  React.useLayoutEffect(() => {
    lockedPickRef.current = onLockedPick;
  });
  const savedConfigId = useSelector(selectSavedForecastConfigId);
  const savedVariable = useSelector(selectForecastVariable);

  // --- The catalog --------------------------------------------------------
  const {available, lockedConfigs} = React.useMemo(
    () => splitForecastCatalog(catalog.configs),
    [catalog.configs],
  );
  const models = React.useMemo(
    () => forecastCatalogModels(catalog.configs),
    [catalog.configs],
  );
  const {usable: railVariables, locked: lockedVariables} = React.useMemo(
    () => forecastRailVariables(models),
    [models],
  );
  const ready = catalog.status === 'ready';
  const bounds = view.bounds ?? FORECAST_MAP_SELECTION_WORLD;

  // --- Resolution -----------------------------------------------------------
  const urlConfigRef = url.config;
  const urlVariableKey = url.variable;
  const resolution = React.useMemo(
    () =>
      ready
        ? resolveForecastMapSelection({
            available,
            lockedConfigs,
            models,
            railVariables,
            url: {config: urlConfigRef, variable: urlVariableKey},
            savedConfigId,
            savedVariable,
          })
        : null,
    [
      ready,
      available,
      lockedConfigs,
      models,
      railVariables,
      urlConfigRef,
      urlVariableKey,
      savedConfigId,
      savedVariable,
    ],
  );
  const selection = resolution?.selection ?? null;

  // --- Auto -----------------------------------------------------------------
  const variable = forecastMapSelectionVariable({
    selection,
    railVariables,
    savedVariable,
  });

  // Auto's model, kept between renders for the hysteresis, and what it was
  // picked for: a switch the VIEW caused (same variable, still Auto, a new
  // settle) is the one that raises a notice.
  const [auto, setAuto] = React.useState<ForecastAutoPick>({
    key: null,
    variable,
    seq: view.seq,
    isAuto: false,
  });
  const current = auto.variable === variable ? auto.key : null;
  const picked = React.useMemo(
    () => pickAutoModel({models, view: bounds, variable, current}),
    [models, bounds, variable, current],
  );
  // Nothing covers the view: the first model with the variable (the
  // camera then fits it).
  const autoModel = picked?.model ?? fallbackAutoModel(models, variable);
  const autoKey: ForecastModelKey | null = autoModel?.key ?? null;
  const isAuto = selection?.kind === 'auto';

  // The notice raised last, numbered so that each one is its own (its
  // timer, its announcement).
  const [notices, setNotices] = React.useState<{
    count: number;
    notice: ForecastNoticeState | null;
  }>({count: 0, notice: null});
  const raiseNotice = React.useCallback(
    (raised: DistributiveOmit<ForecastNoticeState, 'id'>) =>
      setNotices(({count}) => ({
        count: count + 1,
        notice: {...raised, id: count + 1} as ForecastNoticeState,
      })),
    [],
  );
  const notice = notices.notice;

  // "Cloud cover comes from ECMWF HRES only": raised once Auto shows the
  // variable (the URL write lands a render later), so that it is said in the
  // same commit as the map it explains -- and after it.
  const [onlyFrom, setOnlyFrom] = React.useState<{
    variable: ForecastVariableKey;
    model: string;
  } | null>(null);
  if (
    onlyFrom !== null &&
    selection?.kind === 'auto' &&
    selection.variable === onlyFrom.variable
  ) {
    setOnlyFrom(null);
    raiseNotice({kind: 'variableOnly', ...onlyFrom});
  }
  const next: ForecastAutoPick = {
    key: autoKey,
    variable,
    seq: view.seq,
    isAuto,
  };
  if (
    auto.key !== next.key ||
    auto.variable !== next.variable ||
    auto.isAuto !== next.isAuto ||
    auto.seq !== next.seq
  ) {
    const switched = autoSwitchedNotice({
      previous: auto,
      next,
      models,
      bounds,
    });
    if (switched !== null) {
      raiseNotice(switched);
    }
    setAuto(next);
  }

  // --- What is drawn ----------------------------------------------------------
  const config = drawnForecastConfig(selection, autoModel);
  /** Auto picked the model for the view on screen: the camera stays. */
  const keepView = isAuto && picked !== null;

  const selectedModel =
    selection?.kind === 'model'
      ? (models.find((model) => model.key === selection.modelKey) ?? null)
      : null;

  // --- The hand-picked model left its area ----------------------------------
  const selectionKey = forecastMapSelectionKey(selection);
  const [pickedAt, setPickedAt] = React.useState({
    key: selectionKey,
    userSeq: view.userSeq,
  });
  if (pickedAt.key !== selectionKey) {
    setPickedAt({key: selectionKey, userSeq: view.userSeq});
  }
  const leftArea = forecastLeftArea({
    selectedModel,
    autoModel,
    bounds,
    movedSincePick: view.userSeq > pickedAt.userSeq,
  });

  // --- The model list -------------------------------------------------------
  const list = React.useMemo(
    () => forecastModelList({models, view: bounds, variable}),
    [models, bounds, variable],
  );
  // The Premium model for this view, when finer than Auto's (free accounts).
  const upsellModel = React.useMemo(
    () => premiumUpsellModel({models, view: bounds, variable, autoModel}),
    [models, bounds, variable, autoModel],
  );

  // --- The Banner for an invalid or locked URL config -----------------------
  const raisedBanner = forecastUnavailableBanner({
    config,
    invalidUrlConfig: resolution?.invalidUrlConfig ?? false,
    lockedUrlConfig: resolution?.lockedUrlConfig ?? null,
  });
  const [unavailable, setUnavailable] =
    React.useState<ForecastUnavailable | null>(null);
  const [wasInvalid, setWasInvalid] = React.useState(false);
  const raised = raisedBanner !== null;
  if (raised !== wasInvalid) {
    setWasInvalid(raised);
    if (raised) {
      setUnavailable(raisedBanner);
    }
  }
  const dismissUnavailable = React.useCallback(() => setUnavailable(null), []);

  // --- Writes ---------------------------------------------------------------
  const clearSaved = resolution?.clearSaved ?? false;
  React.useEffect(() => {
    if (clearSaved) {
      dispatch(setSavedForecastConfig(null));
    }
  }, [clearSaved, dispatch]);

  // The URL names what is shown: Auto's variable, or the config's uuid (a
  // legacy id link is rewritten to it here).
  const {config: urlConfig, variable: urlVariable} =
    forecastMapSelectionUrl(selection);
  const resolved = selection !== null;
  React.useEffect(() => {
    if (!resolved) {
      return;
    }
    writeUrl({config: urlConfig, variable: urlVariable});
  }, [resolved, urlConfig, urlVariable, writeUrl]);

  // A choice is the next interaction: whatever a notice said is done with
  // (a keyboard user's choice included -- no key listener for that).
  const clearNotice = React.useCallback(
    () => setNotices((shown) => ({...shown, notice: null})),
    [],
  );

  const pickAuto = React.useCallback(() => {
    clearNotice();
    dispatch(setSavedForecastConfig(null));
    setUnavailable(null);
    writeUrl({config: null, variable});
  }, [clearNotice, dispatch, variable, writeUrl]);

  const pickModel = React.useCallback(
    (key: ForecastModelKey) => {
      const model = models.find((entry) => entry.key === key);
      // A Premium model's: offered, never opened (its manifest 404s).
      if (model?.configs[variable]?.locked === true) {
        lockedPickRef.current?.({kind: 'model', model: model.title});
        return;
      }
      const chosen = model ? usableForecastConfig(model, variable) : null;
      if (chosen === null) {
        return;
      }
      clearNotice();
      dispatch(setSavedForecastConfig(chosen.id));
      dispatch(setForecastVariable(variable));
      setUnavailable(null);
      writeUrl({config: forecastConfigRef(chosen), variable: null});
    },
    [clearNotice, dispatch, models, variable, writeUrl],
  );

  const pickVariable = React.useCallback(
    (chosen: ForecastVariableKey) => {
      // Only Premium models have it: offered, the variable shown stays.
      if (lockedVariables.includes(chosen)) {
        lockedPickRef.current?.({kind: 'variable', variable: chosen});
        return;
      }
      clearNotice();
      dispatch(setForecastVariable(chosen));
      setUnavailable(null);
      // A hand-picked model that has it stays.
      const kept = selectedModel
        ? usableForecastConfig(selectedModel, chosen)
        : null;
      if (kept !== null) {
        dispatch(setSavedForecastConfig(kept.id));
        writeUrl({config: forecastConfigRef(kept), variable: null});
        return;
      }
      // Else Auto on it -- saying so when one model alone has it.
      dispatch(setSavedForecastConfig(null));
      writeUrl({config: null, variable: chosen});
      setOnlyFrom(variableOnlyNotice(models, chosen));
    },
    [clearNotice, dispatch, lockedVariables, models, selectedModel, writeUrl],
  );

  // --- Notices: gone after 6 s, or on the next interaction ------------------
  const noticeId = notice?.id ?? null;
  React.useEffect(() => {
    if (noticeId === null) {
      return;
    }
    const dismiss = () =>
      setNotices((shown) =>
        shown.notice?.id === noticeId ? {...shown, notice: null} : shown,
      );
    const timer = setTimeout(dismiss, FORECAST_NOTICE_TIMEOUT_MS);
    const unsubscribe = subscribeNoticeDismiss?.(dismiss);
    return () => {
      clearTimeout(timer);
      unsubscribe?.();
    };
  }, [noticeId, subscribeNoticeDismiss]);

  const nothingAvailable = ready && available.length === 0;
  return {
    catalog,
    /** The catalog loaded, but nothing in it can be drawn. */
    empty: nothingAvailable && lockedConfigs.length === 0,
    /** Maps exist, but every one needs Premium (#327 MR7a). */
    lockedOnly: nothingAvailable && lockedConfigs.length > 0,
    /**
     * The catalog holds locked presets: this account is on the free tier
     * (Auto's description says which model it gets).
     */
    hasLocked: lockedConfigs.length > 0,
    selection,
    /** The config drawn (null while resolving). */
    config,
    /** Auto picked for the view on screen: the camera keeps it. */
    keepView,
    /** The rail variable shown (or Auto's, for a pinned map). */
    variable,
    railVariables,
    /** Variables only locked presets have: on the rail, offered. */
    lockedVariables,
    models,
    /** The model Auto draws (or would) for the view. */
    autoModel,
    selectedModel,
    list,
    /** The Premium model worth naming for the view, else null. */
    upsellModel,
    notice,
    leftArea,
    unavailable,
    dismissUnavailable,
    pickAuto,
    pickModel,
    pickVariable,
  };
};

export default useForecastMapSelection;
